import * as React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { CustomThemeContext } from '../../../../context/theme/custom-theme-context';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { IHotspotAnalysis } from '../../../../shared/services/analytics/hotspots.model';
import { HotspotAnalysisBody } from '../../../presentation-components/analytics-components/hotspot-analysis-body';
import { useDrillDimensionLabel } from '../../../presentation-components/analytics-components/use-drill-dimension-label';
import { HOTSPOT_DIMENSIONS, HOTSPOT_ITEM_LIMIT } from './analytics-hotspots-page';

/**
 * Print page for the Hotspot Analysis PDF, rendered by the backend in headless Chrome as the
 * requesting user (GET /analytics/hotspots/:reportPeriodId/pdf). It shows the view described by its
 * query (dim, basis, compare, rows) and never reads the global period selector. The backend waits
 * for data-hotspot-pdf-ready before printing, or fails fast on data-hotspot-pdf-error.
 */
export const AnalyticsHotspotsPrintPage = () => {
  const { reportPeriodId } = useParams();
  const [searchParams] = useSearchParams();
  const userDetailsContext = React.useContext(UserDetailsContext);
  const screenLabelsContext = React.useContext(ScreenLabelsContext);
  const customThemeContext = React.useContext(CustomThemeContext);
  const dimensionLabel = useDrillDimensionLabel();
  const analyticsService = React.useMemo(() => new AnalyticsService(), []);

  const requestedDimension = searchParams.get('dim');
  const scopeTwoMethod: 'market' | 'location' = searchParams.get('basis') === 'market' ? 'market' : 'location';
  const comparisonReportPeriodId = searchParams.get('compare') || undefined;
  const showAllRanked = searchParams.get('rows') === 'all';

  const [data, setData] = React.useState<IHotspotAnalysis>();
  const [loadFailed, setLoadFailed] = React.useState<boolean>(false);
  // The logo source that has finished loading (or failed to), so a late change of source cannot
  // leave the page marked ready before the new image is in.
  const [settledLogoSrc, setSettledLogoSrc] = React.useState<string>();
  const [ready, setReady] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!reportPeriodId) {
      setLoadFailed(true);
      return;
    }
    let cancelled = false;
    analyticsService
      .getHotspots(reportPeriodId, { scopeTwoMethod, comparisonReportPeriodId, limit: HOTSPOT_ITEM_LIMIT })
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [analyticsService, reportPeriodId, scopeTwoMethod, comparisonReportPeriodId]);

  // Same fallback as the page: a dimension the organisation does not get becomes the default.
  const matrixDimension = React.useMemo(() => {
    const requested = requestedDimension && HOTSPOT_DIMENSIONS.includes(requestedDimension) ? requestedDimension : 'category';
    if (data?.matrixDimensions?.length && !data.matrixDimensions.includes(requested)) {
      return data.defaultDimension || data.matrixDimensions[0];
    }
    return requested;
  }, [data, requestedDimension]);

  // CustomThemeContext.logoUrl follows the domain the page is served on, and this render is always
  // served on the app's own domain. So a partner-branded session (a partner inside a client, or a
  // partner's client) takes the partner's logo from the user details, the rule the left rail uses,
  // and shows no logo rather than the default one when the partner has none.
  const userDetails = userDetailsContext.userDetails;
  const partnerBranded = (userDetails?.isPartner && userDetails?.isLoggedInAsClient) || (!userDetails?.isPartner && !!userDetails?.partnerOrganisation);
  const logoSrc = partnerBranded ? userDetails?.partnerOrganisation?.partnerLogoFilepath : customThemeContext.logoUrl;

  // Custom screen labels arrive on their own request; getLabel answers '' until they have.
  const labelsLoaded = screenLabelsContext.getLabel('location') !== '';

  // Ready once the data, the logo and the labels are in, after one frame so the last render has
  // been painted.
  const logoSettled = !logoSrc || settledLogoSrc === logoSrc;
  React.useEffect(() => {
    if (!data || !labelsLoaded || !logoSettled) return;
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, [data, labelsLoaded, logoSettled]);

  const headerFacts = data
    ? [
      data.reportPeriod.friendlyName,
      data.scopeTwoMethod === 'market' ? 'Market-based' : 'Location-based',
      data.comparison ? `Compared with ${data.comparison.friendlyName}` : 'No comparison period',
      `Analysed by ${dimensionLabel(matrixDimension)}`,
      `Generated ${moment().format('D MMMM YYYY')}`,
    ]
    : [];

  return (
    <Box
      data-hotspot-pdf-ready={ready ? 'true' : undefined}
      data-hotspot-pdf-error={loadFailed ? 'true' : undefined}
      sx={{ width: '100%', boxSizing: 'border-box', p: 3 }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={3} sx={{ mb: 3, breakInside: 'avoid' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='h4' sx={{ color: 'text.primary' }}>Emissions Hotspot Analysis</Typography>
          {userDetails?.organisation?.name && (
            <Typography variant='h6' sx={{ color: 'text.primary', mt: 0.5 }}>{userDetails.organisation.name}</Typography>
          )}
          {headerFacts.length > 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              {headerFacts.join(' · ')}
            </Typography>
          )}
        </Box>
        {logoSrc && (
          <Box sx={{ height: 56, width: 220, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <img
              src={logoSrc}
              alt=''
              onLoad={() => setSettledLogoSrc(logoSrc)}
              onError={() => setSettledLogoSrc(logoSrc)}
              style={{ height: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
      </Stack>

      {loadFailed && (
        <Typography color='error'>Failed to load hotspot analysis. Please try again.</Typography>
      )}

      {data && (
        data.totals.total <= 0 ? (
          <Typography color='text.secondary'>No emissions recorded for this report period.</Typography>
        ) : (
          <HotspotAnalysisBody
            data={data}
            matrixDimension={matrixDimension}
            dimensionLabel={dimensionLabel}
            showAllRanked={showAllRanked}
            printMode
          />
        )
      )}
    </Box>
  );
};
